import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Trash2, RotateCcw, X, Mail, Phone, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";

interface Contact { full_name: string | null; email: string | null; phone: string | null; }
interface RequestRow {
  id: string; created_at: string; status: string; category: string | null;
  is_colors_only: boolean; description: string | null;
  estimated_price_min: number | null; estimated_price_max: number | null;
  deleted: boolean; contact: Contact; images_count: number;
}

const STATUS: Record<string, { label: string; cls: string }> = {
  new: { label: "Nuova", cls: "bg-secondary text-black" },
  in_review: { label: "In revisione", cls: "bg-blue-500/20 text-blue-300 border border-blue-500/40" },
  quoted: { label: "Preventivo", cls: "bg-primary/20 text-primary border border-primary/40" },
  accepted: { label: "Accettata", cls: "bg-green-500/20 text-green-300 border border-green-500/40" },
  rejected: { label: "Rifiutata", cls: "bg-red-500/20 text-red-300 border border-red-500/40" },
  done: { label: "Completata", cls: "bg-white/10 text-muted-foreground border border-white/20" },
};
const STATUS_KEYS = Object.keys(STATUS);
const CATEGORY: Record<string, string> = { baby: "Baby", kids: "Kids", adulti: "Adulti" };

const fmtPrice = (a: number | null, b: number | null) => {
  const r = (n: number) => `€${Math.round(n)}`;
  if (a != null && b != null) return `${r(a)} – ${r(b)}`;
  if (a != null) return `Da ${r(a)}`;
  return "Su richiesta";
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const FILTERS = [
  { key: "", label: "Tutte" },
  ...STATUS_KEYS.map((k) => ({ key: k, label: STATUS[k].label })),
];

export default function RequestsPage() {
  const [rows, setRows] = useState<RequestRow[] | null>(null);
  const [filter, setFilter] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    const params: any = { include_deleted: showTrash };
    if (filter) params.status = filter;
    api.get("/admin/requests", { params }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [filter, showTrash]);

  useEffect(() => { load(); }, [load]);

  const emptyTrash = async () => {
    if (!window.confirm("Svuotare il cestino? Tutte le richieste nel cestino verranno eliminate definitivamente.")) return;
    try {
      const { data } = await api.post("/admin/requests/empty-trash");
      toast.success(data.message || "Cestino svuotato");
      load();
    } catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="requests-page">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">Fase 5 · Richieste</p>
      <h1 className="font-display font-black tracking-tighter text-4xl mb-1">Richieste</h1>
      <p className="text-muted-foreground text-sm mb-8">Gestisci le richieste ricevute dal configuratore.</p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`req-filter-${f.key || "all"}`} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold border transition-colors ${
              filter === f.key ? "border-primary text-foreground bg-white/5" : "border-white/10 text-muted-foreground hover:text-secondary"
            }`}>
            {f.label}
          </button>
        ))}
        <button data-testid="req-toggle-trash" onClick={() => setShowTrash((v) => !v)}
          className={`ml-auto px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold border transition-colors flex items-center gap-2 ${
            showTrash ? "border-red-500/50 text-red-300 bg-red-500/10" : "border-white/10 text-muted-foreground hover:text-foreground"
          }`}>
          <Trash2 className="h-3.5 w-3.5" /> Cestino
        </button>
        {showTrash && rows && rows.length > 0 && (
          <button data-testid="req-empty-trash" onClick={emptyTrash}
            className="px-4 py-2 text-xs uppercase tracking-[0.12em] font-bold border border-red-500/50 text-red-300 bg-red-500/10 hover:bg-red-500/20 transition-colors flex items-center gap-2">
            <Trash2 className="h-3.5 w-3.5" /> Svuota cestino
          </button>
        )}
      </div>

      {rows === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/15 p-16 text-center" data-testid="requests-empty">
          <p className="font-display font-bold text-xl mb-1">Nessuna richiesta</p>
          <p className="text-muted-foreground text-sm">Le richieste inviate dal configuratore appariranno qui.</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto" data-testid="requests-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Data", "Cliente", "Categoria", "Stato", "Prezzo", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`request-row-${r.id}`} onClick={() => setOpenId(r.id)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold">{r.contact.full_name || "—"}</span>
                    {r.images_count > 0 && <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><ImageIcon className="h-3 w-3" />{r.images_count}</span>}
                  </td>
                  <td className="px-4 py-3">{r.is_colors_only ? "Solo colori" : (CATEGORY[r.category || ""] || "—")}</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold ${STATUS[r.status]?.cls || ""}`}>{STATUS[r.status]?.label || r.status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtPrice(r.estimated_price_min, r.estimated_price_max)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <RequestDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </motion.div>
  );
}

const RequestDetail: React.FC<{ id: string; onClose: () => void; onChanged: () => void }> = ({ id, onClose, onChanged }) => {
  const [data, setData] = useState<any>(null);
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/requests/${id}`).then(({ data }) => {
      setData(data); setStatus(data.request.status); setNotes(data.request.admin_notes || "");
    }).catch(() => {});
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/requests/${id}`, { status, admin_notes: notes });
      toast.success("Richiesta aggiornata"); onChanged(); onClose();
    } catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const del = async () => {
    try { await api.delete(`/admin/requests/${id}`); toast.success("Spostata nel cestino"); onChanged(); onClose(); }
    catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const restore = async () => {
    try { await api.post(`/admin/requests/${id}/restore`); toast.success("Ripristinata"); onChanged(); onClose(); }
    catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const permanentDel = async () => {
    if (!window.confirm("Eliminare DEFINITIVAMENTE questa richiesta con contatti e immagini? L'azione non e' reversibile.")) return;
    try { await api.delete(`/admin/requests/${id}/permanent`); toast.success("Eliminata definitivamente"); onChanged(); onClose(); }
    catch (err: any) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const req = data?.request; const contact = data?.contact; const labels = data?.labels || {};
  const design = req?.is_colors_only ? "Solo colori" : (req?.design_other ? `Altro: ${req.design_other}` : labels.theme);
  const model = req?.model_other ? `Altro: ${req.model_other}` : labels.model;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="request-detail">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <motion.div initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.25 }}
        className="relative w-full max-w-lg bg-background border-l border-white/10 h-full overflow-y-auto">
        {!data ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Richiesta</p>
                <p className="font-mono text-xs text-secondary">{id}</p>
              </div>
              <button data-testid="request-detail-close" onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X className="h-5 w-5" /></button>
            </div>

            <div className="border border-white/10 p-5 mb-6">
              <p className="font-display font-bold text-lg">{contact?.full_name}</p>
              <div className="flex flex-col gap-1 mt-2">
                {contact?.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary"><Mail className="h-3.5 w-3.5" />{contact.email}</a>}
                {contact?.phone && <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary"><Phone className="h-3.5 w-3.5" />{contact.phone}</a>}
              </div>
            </div>

            <div className="divide-y divide-white/10 border-y border-white/10 mb-6 text-sm">
              <Row label="Categoria" value={labels.category} />
              <Row label="Modello" value={model} />
              <Row label="Tipo di design" value={design} />
              {!req.is_colors_only && <>
                <Row label="Area" value={labels.area} />
                <Row label="Realizzazione" value={labels.type} />
                <Row label="Dettaglio" value={labels.detail} />
              </>}
              <Row label="Stima prezzo" value={fmtPrice(req.estimated_price_min, req.estimated_price_max)} />
              <Row label="Ricevuta il" value={fmtDate(req.created_at)} />
            </div>

            {req.description && (
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Descrizione</p>
                <p className="text-sm whitespace-pre-wrap">{req.description}</p>
              </div>
            )}

            {data.images?.length > 0 && (
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Immagini</p>
                <div className="grid grid-cols-4 gap-2" data-testid="request-detail-images">
                  {data.images.map((img: any) => (
                    <a key={img.id} href={mediaUrl(img.image_url)} target="_blank" rel="noreferrer">
                      <img src={mediaUrl(img.image_url)} alt="" className="aspect-square w-full object-cover border border-white/10" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4 border-t border-white/10 pt-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Stato</label>
                <select data-testid="request-status-select" value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary">
                  {STATUS_KEYS.map((k) => <option key={k} value={k} className="bg-background">{STATUS[k].label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Note interne</label>
                <textarea data-testid="request-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                  className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary" />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="request-save-button" onClick={save} disabled={saving}
                  className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
                </button>
                {req.deleted_at ? (
                  <button data-testid="request-restore-button" onClick={restore} className="px-4 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-green-300 hover:border-green-500/50 flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Ripristina</button>
                ) : (
                  <button data-testid="request-delete-button" onClick={del} className="px-4 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-red-300 hover:border-red-500/50 flex items-center gap-2"><Trash2 className="h-4 w-4" /> Cestino</button>
                )}
              </div>
              <button data-testid="request-permanent-delete-button" onClick={permanentDel}
                className="w-full mt-1 px-4 py-3 border border-red-500/30 text-[11px] uppercase tracking-[0.12em] font-bold text-red-400 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2">
                <Trash2 className="h-3.5 w-3.5" /> Elimina definitivamente (GDPR)
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="flex items-center justify-between px-1 py-3 gap-4">
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{value || "—"}</span>
  </div>
);
